import { rootApi } from '../rootApi';
import {
  ScanFinding,
  Replacement,
  Presentation,
  SummaryAction,
  AnonymisationLevel,
  PresentationSummary,
  PresentationTemplate,
  ExportPresentationResult,
  PresentationExportFormat,
  ExportPresentationRequest,
  CreatePresentationRequest,
  UpdatePresentationRequest,
  PresentationTemplateSummary,
} from './presentations.types';

// Candidate presentations (JT1-1886). Tags are added here rather than in rootApi's shared list.
const api = rootApi.enhanceEndpoints({ addTagTypes: ['Presentations', 'Presentation', 'PresentationTemplates'] });

export const presentationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCandidatePresentations: builder.query<PresentationSummary[], string>({
      query: (candidateId) => ({ url: `/my/candidates/${candidateId}/presentations` }),
      providesTags: (_result, _error, candidateId) => [{ type: 'Presentations', id: candidateId }],
    }),

    getPresentation: builder.query<Presentation, string>({
      query: (id) => ({ url: `/my/presentations/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'Presentation', id }],
    }),

    createPresentation: builder.mutation<Presentation, CreatePresentationRequest>({
      query: ({ candidateId, ...data }) => ({ url: `/my/candidates/${candidateId}/presentations`, method: 'POST', data }),
      invalidatesTags: (_result, _error, { candidateId }) => [{ type: 'Presentations', id: candidateId }],
    }),

    // The builder keeps its own copy while editing, so a save updates the cache rather than refetching.
    updatePresentation: builder.mutation<Presentation, UpdatePresentationRequest>({
      query: ({ id, ...data }) => ({ url: `/my/presentations/${id}`, method: 'PATCH', data }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(presentationsApi.util.upsertQueryData('getPresentation', id, data));
          dispatch(presentationsApi.util.invalidateTags([{ type: 'Presentations', id: data.organization_candidate_id }]));
        } catch {
          // The caller handles the error (409 shows the conflict banner).
        }
      },
    }),

    deletePresentation: builder.mutation<void, { id: string; candidateId: string }>({
      query: ({ id }) => ({ url: `/my/presentations/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { candidateId }) => [{ type: 'Presentations', id: candidateId }],
    }),

    duplicatePresentation: builder.mutation<Presentation, { id: string; candidateId: string }>({
      query: ({ id }) => ({ url: `/my/presentations/${id}/duplicate`, method: 'POST' }),
      invalidatesTags: (_result, _error, { candidateId }) => [{ type: 'Presentations', id: candidateId }],
    }),

    refreshPresentationBlock: builder.mutation<Presentation, { id: string; blockId: string; version: number }>({
      query: ({ id, blockId, version }) => ({ url: `/my/presentations/${id}/blocks/${blockId}/refresh`, method: 'POST', data: { version } }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(presentationsApi.util.upsertQueryData('getPresentation', id, data));
        } catch {
          // Shown by the caller.
        }
      },
    }),

    // An image uploaded through /upload/file, kept for presentations (image blocks, header and footer banners).
    // An AI draft of a text section; not saved, the builder puts it in the block (JT1-1886, A6).
    draftPresentationText: builder.mutation<{ html: string }, { id: string; blockId: string; action: SummaryAction; current_html: string }>({
      query: ({ id, blockId, ...data }) => ({ url: `/my/presentations/${id}/blocks/${blockId}/ai-draft`, method: 'POST', data }),
    }),

    claimPresentationImage: builder.mutation<{ file_id: string }, string>({
      query: (fileId) => ({ url: '/my/presentations/images', method: 'POST', data: { file_id: fileId } }),
    }),

    savePresentationAsTemplate: builder.mutation<PresentationTemplate, { id: string; name: string; is_default?: boolean }>({
      query: ({ id, ...data }) => ({ url: `/my/presentations/${id}/save-as-template`, method: 'POST', data }),
      invalidatesTags: ['PresentationTemplates'],
    }),

    suggestReplacements: builder.mutation<{ level: AnonymisationLevel; ai_used: boolean; ai_restricted?: boolean; replacements: Replacement[] }, { id: string; level: AnonymisationLevel }>({
      query: ({ id, level }) => ({ url: `/my/presentations/${id}/anonymisation/suggest`, method: 'POST', data: { level } }),
    }),

    scanPresentation: builder.mutation<{ findings: ScanFinding[]; ai_checked?: boolean }, { id: string; format?: PresentationExportFormat }>({
      query: ({ id, format }) => ({ url: `/my/presentations/${id}/scan`, method: 'POST', data: { format } }),
    }),

    exportPresentation: builder.mutation<ExportPresentationResult, ExportPresentationRequest>({
      query: ({ id, ...data }) => ({ url: `/my/presentations/${id}/exports`, method: 'POST', data }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(presentationsApi.util.invalidateTags([{ type: 'Presentation', id }, 'Presentations']));
        } catch {
          // A refused export (findings to acknowledge) is handled by the export dialog.
        }
      },
    }),

    getPresentationExportDownload: builder.mutation<{ url: string }, { id: string; exportId: string }>({
      query: ({ id, exportId }) => ({ url: `/my/presentations/${id}/exports/${exportId}/download` }),
    }),

    getPresentationTemplates: builder.query<PresentationTemplateSummary[], void>({
      query: () => ({ url: '/presentation-templates' }),
      providesTags: ['PresentationTemplates'],
    }),

    getPresentationTemplate: builder.query<PresentationTemplate, string>({
      query: (key) => ({ url: `/presentation-templates/${key}` }),
      providesTags: (_result, _error, key) => [{ type: 'PresentationTemplates', id: key }],
    }),

    duplicatePresentationTemplate: builder.mutation<PresentationTemplate, { key: string; name?: string; is_default?: boolean }>({
      query: ({ key, ...data }) => ({ url: `/presentation-templates/${key}/duplicate`, method: 'POST', data }),
      invalidatesTags: ['PresentationTemplates'],
    }),

    updatePresentationTemplate: builder.mutation<PresentationTemplate, { id: string; name?: string; definition?: PresentationTemplate['definition']; is_default?: boolean }>({
      query: ({ id, ...data }) => ({ url: `/presentation-templates/${id}`, method: 'PATCH', data }),
      invalidatesTags: ['PresentationTemplates'],
    }),

    deletePresentationTemplate: builder.mutation<void, string>({
      query: (id) => ({ url: `/presentation-templates/${id}`, method: 'DELETE' }),
      invalidatesTags: ['PresentationTemplates'],
    }),
  }),
});

export const {
  useGetCandidatePresentationsQuery,
  useGetPresentationQuery,
  useCreatePresentationMutation,
  useUpdatePresentationMutation,
  useDeletePresentationMutation,
  useDuplicatePresentationMutation,
  useRefreshPresentationBlockMutation,
  useClaimPresentationImageMutation,
  useDraftPresentationTextMutation,
  useSavePresentationAsTemplateMutation,
  useSuggestReplacementsMutation,
  useScanPresentationMutation,
  useExportPresentationMutation,
  useGetPresentationExportDownloadMutation,
  useGetPresentationTemplatesQuery,
  useGetPresentationTemplateQuery,
  useDuplicatePresentationTemplateMutation,
  useUpdatePresentationTemplateMutation,
  useDeletePresentationTemplateMutation,
} = presentationsApi;
